import _ from "lodash";

import { getConvertedCurrency } from "lib/shared/currency/getConvertedCurrency";
import { getCurrencyOffInstance } from "lib/shared/currency/getCurrencyField";
import { PayPointSettings } from "lib/shared/pay-points/types";
import { FieldType } from "prisma/cm/client";

import { FieldDefinition, UsageModel } from "../../fieldDefinition";
import { FieldValueResolver } from "../../FieldValueResolver";
import { getFieldTypeForPayPointGroup } from "../../getFieldTypeForPayPointGroup";
import { FieldSourceType } from "../../types";

export function fromPayPointSettings(payPointSettings: PayPointSettings): FieldDefinition[] {
  const payPointGroups = {} as Record<string, keyof PayPointSettings>;

  // @ts-expect-error TS2322
  return _(payPointSettings)
    .keys()
    .map((payPointGroupName: keyof PayPointSettings) => {
      const payPointGroup = payPointSettings[payPointGroupName];
      return _(payPointGroup)
        .map((payPoint) => {
          payPointGroups[payPoint.id] = payPointGroupName;
          const payPointId = payPoint.id;

          const fieldDefinitionType = getFieldTypeForPayPointGroup(payPointGroupName);
          const isRecurringPayment =
            fieldDefinitionType.includes(FieldType.DOLLAR) &&
            (payPointGroupName === "salary" || payPointGroupName === "totalPay");

          const def = new FieldDefinition({
            id: payPointId,
            fieldSourceType: FieldSourceType.PAY_POINT,
            baseModel: UsageModel.Range,
            isRecurringPayment,
            valueGetter: (range, context, rawInstance) => {
              if (!range || !range.payPoints) {
                return null;
              }

              if (!context.exchangeRates) {
                throw new Error("Missing exchange rates for pay point settings");
              }

              let payPointValue =
                range.payPoints[payPointGroupName as keyof typeof range.payPoints]?.[payPointId];

              // Bonus pay-points are sometimes uploaded as values greater than 1.
              // So, to show as a percents we divide by 100.
              if (payPointGroupName === "bonus" && payPointValue >= 1) {
                payPointValue = payPointValue / 100;
              }

              const fieldType = getFieldTypeForPayPointGroup(payPointGroupName);

              // in the case where users' currency code is a different than the range's currency code,
              // we need to return the value in the users currency (same applies for proposals)
              if (
                fieldType.includes(FieldType.DOLLAR) &&
                (FieldValueResolver.validateAsUser(rawInstance!) ||
                  FieldValueResolver.validateAsProposal(rawInstance!))
              ) {
                return getConvertedCurrency({
                  value: payPointValue,
                  options: {
                    sourceCurrency: getCurrencyOffInstance(range),
                    targetCurrency: getCurrencyOffInstance(rawInstance),
                    exchangeRates: context.exchangeRates,
                  },
                }).raw;
              }

              return payPointValue;
            },
            fieldTypes: fieldDefinitionType,
          });

          def.label = payPoint.label;
          def.textPopulated = true;

          return def;
        })
        .valueOf();
    })
    .flatten()
    .valueOf();
}
